/**
 * KPI ダッシュ画面（PC 専用 / Phase 3 / issue #197）。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退。
 * 製品フィルタ・賃貸 4 KPI 表示は削除済）。
 *
 * - OPS-KPI-1〜3 推移 + 目標ゴーストライン（OPS-KPI-9）
 *
 * spec/ai-inbox-core/admin-ops/design.md §6.2 / §6.3 参照。
 */
import { useQuery } from '@tanstack/react-query';
import { fetchKpiOverview } from '../../lib/kpi-api';
import { KpiOverviewChart } from './KpiOverviewChart';

function formatYen(n: number): string {
  return `¥${n.toLocaleString('ja-JP')}`;
}

function formatPercent(n: number | null): string {
  return n == null ? '—' : `${(n * 100).toFixed(1)}%`;
}

export function KpiDashboardPage({ apiBaseUrl }: { apiBaseUrl: string }) {
  const overviewQuery = useQuery({
    queryKey: ['admin-ops-kpi-overview'],
    queryFn: () => fetchKpiOverview(apiBaseUrl),
  });

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-wider text-slate-500">
          ALYS Internal — Operations
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">
          KPI ダッシュボード <span className="text-sm text-slate-400">[内部運用画面]</span>
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          OPS-KPI-1〜3 + 9 + 10。営業受付カウンターの課金社数 / MRR / 月次チャーン率を 12 ヶ月推移で
          表示。目標値は破線（ゴーストライン）で重ねる。
        </p>
      </header>

      {overviewQuery.isLoading && <p className="text-sm text-slate-500">KPI を集計中…</p>}
      {overviewQuery.isError && (
        <p className="text-sm text-rose-600">
          KPI 集計に失敗しました: {(overviewQuery.error as Error).message}
        </p>
      )}

      {overviewQuery.data && (
        <>
          <section className="mb-8 grid grid-cols-3 gap-4">
            <SummaryCard
              label="課金社数（当月）"
              value={`${overviewQuery.data.current.paidWorkspaceCount}`}
              hint={overviewQuery.data.productLabel}
            />
            <SummaryCard
              label="MRR（当月）"
              value={formatYen(overviewQuery.data.current.mrr)}
              hint={overviewQuery.data.productLabel}
            />
            <SummaryCard
              label="月次チャーン率（当月）"
              value={formatPercent(overviewQuery.data.current.monthlyChurnRate)}
              hint="前月末 active 0 → '—'"
            />
          </section>

          <KpiOverviewChart
            overview={overviewQuery.data}
            paidWorkspaceLabel="課金社数"
            mrrLabel="MRR"
          />
        </>
      )}
    </main>
  );
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-400">{hint}</p>
    </div>
  );
}
